/**
 * VisibilityContext.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { setGuestPageVisibility } from '../utils/postLogoutLocation';

const VisibilityContext = createContext();

export const useVisibility = () => {
  const context = useContext(VisibilityContext);
  if (!context) {
    throw new Error('useVisibility must be used within a VisibilityProvider');
  }
  return context;
};

export const VisibilityProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVisibilityData();
  }, []);

  const fetchVisibilityData = async () => {
    try {
      setLoading(true);
      // Set default pages if API fails
      const defaultPages = [
        { page_key: 'home', page_name: 'Home Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'shop', page_name: 'Shop/Products', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'about', page_name: 'About Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'contact', page_name: 'Contact Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'checkout', page_name: 'Checkout', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'login', page_name: 'Login Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'register', page_name: 'Registration Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 }
      ];
      
      try {
        const [pagesResponse, settingsResponse] = await Promise.all([
          fetch('/api/visibility/pages'),
          fetch('/api/visibility/public/settings')
        ]);

        if (pagesResponse.ok && settingsResponse.ok) {
          const pagesData = await pagesResponse.json();
          const settingsData = await settingsResponse.json();

          const nextPages = pagesData.pages || defaultPages;
          setPages(nextPages);
          setGuestPageVisibility(nextPages);
          setSettings(settingsData.settings || {});
        } else {
          console.warn('VisibilityContext: API not available, using default settings');
          setPages(defaultPages);
          setGuestPageVisibility(defaultPages);
          setSettings({});
        }
      } catch (apiError) {
        console.warn('VisibilityContext: API error, using default settings:', apiError);
        setPages(defaultPages);
        setGuestPageVisibility(defaultPages);
        setSettings({});
      }
    } catch (err) {
      console.error('Error in fetchVisibilityData:', err);
      setError(err.message);
      // Set default pages even on error
      const fallbackPages = [
        { page_key: 'home', page_name: 'Home Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'shop', page_name: 'Shop/Products', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'about', page_name: 'About Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'contact', page_name: 'Contact Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'checkout', page_name: 'Checkout', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'login', page_name: 'Login Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 },
        { page_key: 'register', page_name: 'Registration Page', is_visible: 1, is_visible_to_guests: 1, is_visible_to_customers: 1, is_visible_to_wholesalers: 1, is_visible_to_admins: 1 }
      ];
      setPages(fallbackPages);
      setGuestPageVisibility(fallbackPages);
    } finally {
      setLoading(false);
    }
  };

  const isPageVisible = (pageKey) => {
    // Always show pages while loading to prevent flashing
    if (loading) {
      return true;
    }
    
    // If no pages loaded yet, show by default
    if (!pages || pages.length === 0) {
      return true;
    }
    
    const page = pages.find(p => p.page_key === pageKey);
    if (!page) {
      return true; // Show page if not found in visibility settings
    }

    // Check if page is generally visible
    if (!page.is_visible) {
      return false;
    }

    // Check role-based visibility
    if (!currentUser) {
      // Guest user
      return page.is_visible_to_guests === 1;
    }

    // Check user roles
    const userRoles = currentUser.roles || [];
    const hasAdminRole = userRoles.some(role => 
      (typeof role === 'string' ? role : role.name) === 'admin' || 
      (typeof role === 'string' ? role : role.name) === 'super_admin'
    );
    const hasWholesalerRole = userRoles.some(role => 
      (typeof role === 'string' ? role : role.name) === 'wholesaler'
    );
    const hasCustomerRole = userRoles.some(role => 
      (typeof role === 'string' ? role : role.name) === 'customer'
    );

    // Check role-specific visibility
    if (hasAdminRole) {
      return page.is_visible_to_admins === 1;
    }
    if (hasWholesalerRole) {
      return page.is_visible_to_wholesalers === 1;
    }
    if (hasCustomerRole) {
      return page.is_visible_to_customers === 1;
    }

    // Default: show page to guests if it's visible to guests
    return page.is_visible_to_guests === 1;
  };

  const getPageMaintenanceMessage = (pageKey) => {
    const page = pages.find(p => p.page_key === pageKey);
    return page?.maintenance_message || 'This page is currently unavailable.';
  };

  const getPageRedirectUrl = (pageKey) => {
    const page = pages.find(p => p.page_key === pageKey);
    return page?.redirect_url;
  };

  const isFeatureEnabled = (featureKey) => {
    if (loading) return true; // Enable feature while loading
    
    return settings[featureKey] !== false;
  };

  const getSetting = (settingKey, defaultValue = null) => {
    if (loading) return defaultValue;
    
    return settings[settingKey] !== undefined ? settings[settingKey] : defaultValue;
  };

  const refreshVisibility = () => {
    fetchVisibilityData();
  };

  const value = {
    pages,
    settings,
    loading,
    error,
    isPageVisible,
    getPageMaintenanceMessage,
    getPageRedirectUrl,
    isFeatureEnabled,
    getSetting,
    refreshVisibility
  };

  return (
    <VisibilityContext.Provider value={value}>
      {children}
    </VisibilityContext.Provider>
  );
};
